import React, { useEffect, useState } from "react";
import { pdf } from "@react-pdf/renderer";
import { FileDown, Eye } from "lucide-react";
import { shopApi, formatShopAuthError } from "../../lib/shopAuth";
import ResellerStatementPdf from "../ResellerStatementPdf";
import { COLORS, fmtCHF, fmtDay, openPdf, Panel, Loading, GhostButton, SoftInput } from "./ui";

const iso = (d) => d.toISOString().slice(0, 10);

const Tile = ({ label, value, sub, color = COLORS.primary, testid }) => (
  <div className="p-4" style={{ border: "1px solid rgba(242,236,228,0.08)" }} data-testid={testid}>
    <p className="text-[10px] uppercase tracking-[0.2em]" style={{ color: COLORS.secondary }}>{label}</p>
    <p className="font-mono text-lg mt-1" style={{ color }}>{value}</p>
    {sub && <p className="text-[11px] mt-0.5" style={{ color: COLORS.muted }}>{sub}</p>}
  </div>
);

const BalanceHeader = ({ summary }) => {
  const b = summary.balance;
  const owes = b.balance > 0.01;
  const credit = b.balance < -0.01;
  return (
    <Panel testid="pro-balance">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-5">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em]" style={{ color: COLORS.gold }}>Solde de votre compte</p>
          <p className="text-3xl font-mono mt-2" style={{ color: owes ? COLORS.chili : credit ? COLORS.basil : COLORS.primary }} data-testid="pro-balance-amount">
            {fmtCHF(Math.abs(b.balance))} <span className="text-sm" style={{ color: COLORS.secondary }}>CHF</span>
          </p>
          <p className="text-xs mt-1" style={{ color: COLORS.secondary }}>
            {owes ? "Montant restant à régler" : credit ? "Avoir en votre faveur" : "Compte à jour"} · au {fmtDay(b.as_of)}
          </p>
        </div>
        {summary.open_invoices.count > 0 && (
          <div className="text-right text-xs" style={{ color: COLORS.secondary }} data-testid="pro-open-invoices">
            {summary.open_invoices.count} facture(s) ouverte(s)
            {summary.open_invoices.overdue > 0 && <span style={{ color: COLORS.chili }}> · {summary.open_invoices.overdue} en retard</span>}
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Tile label="Facturé" value={`${fmtCHF(b.total_invoiced)} CHF`} sub={`${b.counts.invoices} facture(s)`} testid="pro-tile-invoiced" />
        <Tile label="Payé" value={`${fmtCHF(b.total_paid)} CHF`} color={COLORS.basil} testid="pro-tile-paid" />
        <Tile label="Avoirs" value={`${fmtCHF(b.total_credited)} CHF`} sub={`${b.counts.credit_notes} avoir(s)`} color={COLORS.gold} testid="pro-tile-credited" />
      </div>
    </Panel>
  );
};

const StatementTable = ({ st }) => (
  <div className="overflow-x-auto mt-4" data-testid="pro-statement-table">
    <table className="w-full text-sm" style={{ color: COLORS.primary }}>
      <thead>
        <tr className="text-[10px] uppercase tracking-[0.15em]" style={{ color: COLORS.secondary }}>
          <th className="text-left py-2">Date</th><th className="text-left">Mouvement</th>
          <th className="text-right">Débit</th><th className="text-right">Crédit</th><th className="text-right">Solde</th>
        </tr>
      </thead>
      <tbody>
        <tr style={{ borderTop: "1px solid rgba(242,236,228,0.08)", color: COLORS.secondary }}>
          <td className="py-2" colSpan={4}>Solde d&apos;ouverture</td><td className="text-right font-mono">{fmtCHF(st.opening_balance)}</td>
        </tr>
        {st.entries.map((e) => (
          <tr key={`${e.type}-${e.id}`} style={{ borderTop: "1px solid rgba(242,236,228,0.06)" }}>
            <td className="py-2 whitespace-nowrap">{fmtDay(e.date)}</td>
            <td>{e.description}</td>
            <td className="text-right font-mono">{e.debit ? fmtCHF(e.debit) : ""}</td>
            <td className="text-right font-mono">{e.credit ? fmtCHF(e.credit) : ""}</td>
            <td className="text-right font-mono">{fmtCHF(e.running_balance)}</td>
          </tr>
        ))}
        <tr style={{ borderTop: "1px solid rgba(242,236,228,0.2)" }}>
          <td className="py-2 font-semibold" colSpan={4}>Solde de clôture</td>
          <td className="text-right font-mono font-semibold" style={{ color: COLORS.gold }} data-testid="pro-statement-closing">{fmtCHF(st.closing_balance)}</td>
        </tr>
      </tbody>
    </table>
  </div>
);

const StatementPanel = () => {
  const today = new Date();
  const [from, setFrom] = useState(iso(new Date(today.getFullYear(), 0, 1)));
  const [to, setTo] = useState(iso(today));
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const fetchSt = async () => {
    const { data } = await shopApi.get("/my-reseller/statement", { params: { from_date: from, to_date: to } });
    return data;
  };
  const show = async () => {
    setBusy("show"); setErr("");
    try { setSt(await fetchSt()); } catch (e) { setErr(formatShopAuthError(e.response?.data?.detail) || e.message); } finally { setBusy(""); }
  };
  const download = async () => {
    setBusy("pdf"); setErr("");
    try {
      const data = await fetchSt();
      const blob = await pdf(<ResellerStatementPdf statement={data} settings={data.settings} />).toBlob();
      openPdf(blob, `Releve-${from}-${to}.pdf`);
    } catch (e) { setErr(formatShopAuthError(e.response?.data?.detail) || e.message); } finally { setBusy(""); }
  };
  return (
    <Panel testid="pro-statement" className="mt-4">
      <p className="text-[10px] uppercase tracking-[0.3em] mb-4" style={{ color: COLORS.gold }}>Relevé de compte</p>
      <div className="flex items-end gap-3 flex-wrap">
        <label className="block w-40"><span className="block text-[10px] uppercase tracking-[0.2em] mb-1.5" style={{ color: COLORS.secondary }}>Du</span>
          <SoftInput type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="pro-statement-from" /></label>
        <label className="block w-40"><span className="block text-[10px] uppercase tracking-[0.2em] mb-1.5" style={{ color: COLORS.secondary }}>Au</span>
          <SoftInput type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="pro-statement-to" /></label>
        <GhostButton onClick={show} loading={busy === "show"} icon={Eye} testid="pro-statement-show">Afficher</GhostButton>
        <GhostButton onClick={download} loading={busy === "pdf"} icon={FileDown} testid="pro-statement-pdf">PDF imprimable</GhostButton>
      </div>
      {err && <p className="text-xs mt-3" style={{ color: COLORS.chili }} data-testid="pro-statement-error">{err}</p>}
      {st && <StatementTable st={st} />}
    </Panel>
  );
};

export const ResellerBalancePanel = () => {
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    shopApi.get("/my-reseller/summary").then(({ data }) => setSummary(data)).catch(() => setSummary(false));
  }, []);
  if (summary === null) return <Loading />;
  if (summary === false) return <Panel><p className="text-sm" style={{ color: COLORS.chili }}>Solde indisponible.</p></Panel>;
  return (
    <div>
      <BalanceHeader summary={summary} />
      <StatementPanel />
    </div>
  );
};

export default ResellerBalancePanel;
